import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Mail } from "lucide-react";
import { useSeo } from "../lib/useSeo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  EMPTY_DETAILS,
  SAMPLE_DETAILS,
  SIGNATURE_LOGO_PATH,
  buildSignatureHtml,
  buildSignatureText,
  copySignature,
  isComplete,
  withSamples,
} from "../lib/emailSignature";

const FIELDS = [
  { key: "name", label: "Full name", type: "text", autoComplete: "name" },
  { key: "title", label: "Job title", type: "text", autoComplete: "organization-title" },
  { key: "phone", label: "Phone", type: "tel", autoComplete: "tel" },
  { key: "email", label: "Email", type: "email", autoComplete: "email" },
];

const STEPS = [
  "Fill in your details, then click Copy Signature.",
  "In Gmail, open the gear icon, then See all settings.",
  "On the General tab, scroll to Signature and click Create new.",
  "Name it, click inside the box and paste (Ctrl+V or Cmd+V).",
  "Pick it under Signature defaults, then click Save Changes at the bottom.",
];

const DetailsForm = ({ details, onChange }) => (
  <div data-testid="signature-details-form" className="grid sm:grid-cols-2 gap-4">
    {FIELDS.map((f) => (
      <div key={f.key}>
        <Label htmlFor={`sig-${f.key}`} className="font-body font-semibold text-pb-ink">{f.label}</Label>
        <Input
          id={`sig-${f.key}`}
          data-testid={`signature-input-${f.key}`}
          type={f.type}
          autoComplete={f.autoComplete}
          placeholder={SAMPLE_DETAILS[f.key]}
          value={details[f.key]}
          onChange={(e) => onChange({ ...details, [f.key]: e.target.value })}
          className="mt-1.5 h-11 rounded-xl border-2 border-pb-ink/10 bg-white font-body text-pb-ink focus-visible:ring-0 focus-visible:border-pb-brand-purple"
        />
      </div>
    ))}
  </div>
);

const ComposePreview = ({ html }) => (
  <div
    data-testid="signature-compose-preview"
    className="rounded-3xl bg-white shadow-[0_20px_60px_-30px_rgba(28,22,40,0.45)] ring-1 ring-pb-ink/10 overflow-hidden"
  >
    <div className="flex items-center gap-2 px-5 py-3 bg-pb-ink text-white text-sm font-body">
      <Mail className="h-4 w-4 text-pb-sun" aria-hidden />
      New message
    </div>
    <div className="px-6 py-3 border-b border-pb-ink/10 text-sm text-pb-soft font-body">To: a-friend@example.com</div>
    <div className="px-6 py-3 border-b border-pb-ink/10 text-sm text-pb-soft font-body">Subject: Hello from Plate Buddies</div>
    <div className="px-6 pt-6 pb-8 font-body text-pb-ink text-[15px] leading-relaxed">
      <p>Hi there,</p>
      <p className="mt-3">Thanks so much for reaching out. Talk soon!</p>
      <p className="mt-6 text-pb-soft">--</p>
      <div data-testid="signature-preview" className="mt-3 overflow-x-auto" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  </div>
);

const CopyButton = ({ copied, disabled, onClick }) => (
  <button
    type="button"
    data-testid="copy-signature-button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-2 rounded-full bg-pb-brand-orange text-white font-display font-black text-base sm:text-lg px-8 py-4 shadow-lg shadow-pb-brand-orange/30 hover:scale-105 active:scale-95 transition-transform disabled:bg-pb-ink/70 disabled:shadow-none disabled:hover:scale-100 disabled:cursor-not-allowed"
  >
    {copied ? <Check className="h-5 w-5" aria-hidden /> : <Copy className="h-5 w-5" aria-hidden />}
    {copied ? "Copied!" : "Copy Signature"}
  </button>
);

const Steps = () => (
  <ol data-testid="signature-gmail-steps" className="space-y-4">
    {STEPS.map((step, i) => (
      <li key={step} className="flex gap-4 items-start font-body text-pb-ink">
        <span className="flex-shrink-0 h-8 w-8 rounded-full bg-pb-brand-purple text-white font-display font-black text-sm flex items-center justify-center">
          {i + 1}
        </span>
        <span className="pt-1">{step}</span>
      </li>
    ))}
  </ol>
);

export default function EmailSignature() {
  useSeo({ title: "Email Signature · Plate Buddies", description: "Plate Buddies email signature", noIndex: true });
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [copied, setCopied] = useState(false);
  const ready = isComplete(details);
  const previewHtml = useMemo(
    () => buildSignatureHtml(withSamples(details), `${window.location.origin}${SIGNATURE_LOGO_PATH}`),
    [details]
  );

  const handleCopy = async () => {
    try {
      await copySignature(buildSignatureHtml(details), buildSignatureText(details));
      setCopied(true);
      toast.success("Signature copied. Paste it into Gmail's signature box.");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Your browser blocked copying. Select the signature in the preview and copy it by hand.");
    }
  };

  return (
    <main data-testid="email-signature-page" className="min-h-screen bg-pb-cream px-5 sm:px-10 py-14 sm:py-20">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-start">
        <section>
          <p className="text-xs font-black uppercase tracking-widest text-pb-brand-teal font-body">Team tools</p>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-pb-brand-purple leading-[1.05]">
            Your Plate Buddies email signature
          </h1>
          <p className="mt-5 text-base sm:text-lg text-pb-soft font-body max-w-xl">
            Type your details and watch the preview update. Built for Gmail, so the layout, colours and links stay put when you paste it in.
          </p>
          <div className="mt-10"><DetailsForm details={details} onChange={setDetails} /></div>
          <div className="mt-8"><ComposePreview html={previewHtml} /></div>
        </section>
        <aside className="lg:sticky lg:top-10 rounded-3xl bg-white/70 ring-1 ring-pb-ink/10 p-8 sm:p-10">
          <CopyButton copied={copied} disabled={!ready} onClick={handleCopy} />
          {!ready && (
            <p data-testid="signature-incomplete-hint" className="mt-3 text-sm text-pb-soft font-body">
              Fill in all four boxes with a valid phone and email to copy.
            </p>
          )}
          <h2 className="mt-10 mb-6 font-display font-black text-base md:text-lg text-pb-ink">Add it to Gmail in 5 steps</h2>
          <Steps />
          <p data-testid="signature-logo-note" className="mt-8 text-sm text-pb-soft font-body">
            The logo loads from platebuddies.com, so it shows up for everyone you email, on phones too.
          </p>
        </aside>
      </div>
    </main>
  );
}
